import { apiClient } from '../../app/api-client';

export type CorrectionStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface CorrectionRequest {
  id: string;
  registrarId: string;
  operatorIdnp: string;
  /** The requester's location holding the lot; '' on a request made before it was recorded. */
  siteId: string;
  medicineCode: string;
  lot: string;
  currentExpiry: string;
  proposedExpiry: string;
  requestComment: string;
  status: CorrectionStatus;
  decidedExpiry: string;
  decisionComment: string;
  decidedByIdnp: string;
  decidedAtIso: string;
  createdAtIso: string;
}

export async function requestCorrection(input: {
  medicineCode: string;
  lot: string;
  proposedExpiry: string;
  /** Optional (owner ruling 2026-09-15): UC51 asks for no reason. */
  requestComment?: string;
}): Promise<CorrectionRequest> {
  const { data } = await apiClient.post<CorrectionRequest>('/corrections', input);
  return data;
}

// The operator's own history, with the medicine the code belongs to resolved by
// the BFF — the same join the administrator's queue reads.
export interface MyCorrectionRequest extends CorrectionRequest {
  medicineName: string;
  medicineForm: string;
  medicineDose: string;
  // The rest of what the wireframe's row and dialog name
  // (SolicitariCorectare.tsx): the medicine's DCI and producer, the
  // Registrar by name, and who asked. '' when the catalogue cannot say, and
  // `operatorName` '' on rows written before specs/030-actor-names.
  medicineDci: string;
  medicineProducer: string;
  // Where the code stands, as the movement dialog tags it (spec 054 T109):
  // derived by the bff from the one rule both screens share. `false`/null for
  // a code the catalogue no longer knows.
  medicineAuthorised: boolean;
  /** `YYYY-MM-DD`; null for an unauthorised code or an expiry the catalogue does not state. */
  medicineAuthorisationExpiry: string | null;
  /** The last day a lapsed authorisation stays reportable (UC20-10's grace); null otherwise. */
  medicineReportableUntil: string | null;
  medicineActive: boolean;
  registrarName: string;
  operatorName: string;
  // The location the request was made from, named; '' when it was not recorded.
  siteName: string;
  // The sites where the lot still has positive stock, named: the Operator's own,
  // every one of the Registrar for its Administrator; empty when it has none there.
  lotLocations: string[];
}

export async function listMyCorrections(): Promise<MyCorrectionRequest[]> {
  const { data } = await apiClient.get<{ requests: MyCorrectionRequest[] }>('/corrections/mine');
  return data.requests ?? [];
}

// caiet UC51-02/03 (spec 035) — the search behind "Solicitare nouă". Grouped by
// medicine because that is the order the reader picks in: the medicine first,
// then which of its lots carries the wrong date.
export interface CorrectableLot {
  medicineCode: string;
  lot: string;
  lotExpiry: string;
  // 'DAY' | 'MONTH': how the lot's expiry was reported; it decides how the date is shown.
  lotExpiryPrecision?: string;
  quantity: string;
  siteCount: number;
  lastReportedAtIso: string;
}

export interface EligibleMedicine {
  code: string;
  name: string;
  pharmaceuticalForm: string;
  dose: string;
  division: string;
  dci: string;
  catalogKnown: boolean;
  authorised: boolean;
  authorisationExpiry: string | null;
  reportableUntil: string | null;
  active: boolean;
  lots: CorrectableLot[];
}

export interface EligibleLots {
  medicines: EligibleMedicine[];
  // The window the server enforces, so the panel states the same deadline it
  // will be refused by instead of printing thirty from memory.
  correctionWindowDays: number;
}

export async function searchCorrectableLots(query: string): Promise<EligibleLots> {
  const { data } = await apiClient.get<EligibleLots>('/corrections/eligible-lots', {
    params: query ? { q: query } : undefined,
  });
  return { medicines: data.medicines ?? [], correctionWindowDays: data.correctionWindowDays };
}

// T062 (docs/shape-ops-expiry-correction-gate.md D2, D3) — the refusal a request
// for each lot would get now, before a date is proposed; '' when it may be asked.
export type LotRefusal =
  | 'LOT_NOT_REPORTED'
  | 'LOT_NO_POSITIVE_STOCK'
  | 'CORRECTION_WINDOW_EXPIRED'
  | 'ALREADY_PENDING';

export interface LotEligibility {
  medicineCode: string;
  lot: string;
  refusal: LotRefusal | '';
}

export interface LotEligibilities {
  lots: LotEligibility[];
  correctionWindowDays: number;
}

export async function assessCorrectionLots(
  lots: ReadonlyArray<{ medicineCode: string; lot: string }>,
): Promise<LotEligibilities> {
  const { data } = await apiClient.post<LotEligibilities>('/corrections/eligibility', { lots });
  return { lots: data.lots ?? [], correctionWindowDays: data.correctionWindowDays };
}

export async function approveCorrection(
  id: string,
  input: { decidedExpiry: string; decisionComment: string },
): Promise<{ updatedStockRows: number }> {
  const { data } = await apiClient.post<{ updatedStockRows: number }>(
    `/corrections/${id}/approve`,
    input,
  );
  return data;
}

export async function rejectCorrection(id: string, reason: string): Promise<void> {
  await apiClient.post(`/corrections/${id}/reject`, { reason });
}

// Owner decision D8 (docs/decizii-paritate-wireframe-2026-09-05.md). All three
// statuses arrive in one answer because two readers of one page load want two
// different ones: the reporting menu badge wants PENDING, Acasa's Atentie
// section wants REJECTED. Counted server-side on the operator's own tenant, so
// it is neither the administrator's count (`/corrections/admin/counts`, which
// refuses a reporter) nor a length taken from `/corrections/mine`, which stops
// at a hundred rows.
export type CorrectionStatusCounts = Record<CorrectionStatus, number>;

export async function countMyCorrections(): Promise<CorrectionStatusCounts> {
  const { data } = await apiClient.get<{ counts: CorrectionStatusCounts }>(
    '/corrections/mine/counts',
  );
  return data.counts;
}
